import React, { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import {
  Users, Scissors, Download, FolderOpen,
  TrendingUp, TrendingDown, Minus, Info,
  Youtube, HardDrive, Library, Trophy,
  CheckCircle, Send, UserCheck, DollarSign,
  Handshake, BarChart2, ExternalLink, ChevronDown,
} from "lucide-react";
import { useAdminCpmCampaignsQuery, useAdminResultsDashboardQuery, useAdminStatsQuery } from "@/generated/graphql";

// ─── Types ───────────────────────────────────────────────────────────────────

interface KpiItem {
  key: string;
  value: number;
  delta: number;
  icon: LucideIcon;
  color: string;
  bg: string;
}

interface FunnelStep {
  step: string;
  users: number;
  pct: number;
}

interface SourceItem {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
  colorClass: string;
}

interface CampaignItem {
  key: string;
  display: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}

// ─── Display metadata ────────────────────────────────────────────────────────

const KPI_ITEMS: KpiItem[] = [
  { key: "users",     value: 1240, delta: +8.3,  icon: Users,      color: "text-blue-500",   bg: "bg-blue-500/10"   },
  { key: "clips",     value: 8763, delta: +14.1, icon: Scissors,   color: "text-purple-500", bg: "bg-purple-500/10" },
  { key: "downloads", value: 2891, delta: -2.4,  icon: Download,   color: "text-green-500",  bg: "bg-green-500/10"  },
  { key: "projects",  value: 4102, delta: +5.7,  icon: FolderOpen, color: "text-orange-500", bg: "bg-orange-500/10" },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const DeltaBadge = ({ delta }: { delta: number }) => {
  if (delta > 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-green-600">
        <TrendingUp className="w-3 h-3" />+{delta.toFixed(1)}%
      </span>
    );
  if (delta < 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-500">
        <TrendingDown className="w-3 h-3" />{delta.toFixed(1)}%
      </span>
    );
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
      <Minus className="w-3 h-3" />0%
    </span>
  );
};

const FunnelCard = ({
  title,
  subtitle,
  steps,
}: {
  title: string;
  subtitle: string;
  steps: FunnelStep[];
}) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </CardHeader>
    <CardContent className="space-y-3">
      {steps.map(({ step, users, pct }, i) => (
        <div key={step} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground flex-shrink-0">
                {i + 1}
              </span>
              <span className="font-medium">{step}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground text-xs">{users.toLocaleString("pt-BR")}</span>
              <Badge
                variant={pct === 100 ? "default" : pct >= 50 ? "secondary" : "outline"}
                className="text-xs w-14 justify-center"
              >
                {pct}%
              </Badge>
            </div>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const SourcesCard = ({
  title,
  sources,
}: {
  title: string;
  sources: SourceItem[];
}) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {sources.map(({ key, label, value, icon: Icon, colorClass }) => (
        <div key={key} className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${colorClass}`} />
              <Icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{label}</span>
            </div>
            <span className="font-semibold">{value}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
              style={{ width: `${value}%` }}
            />
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const RetentionCard = ({
  title,
  data,
  chartLabel,
}: {
  title: string;
  data: { period: string; pct: number }[];
  chartLabel: string;
}) => {
  const config = {
    pct: { label: chartLabel, color: "hsl(var(--primary))" },
  } satisfies ChartConfig;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="h-[180px] w-full">
          <BarChart data={data} barSize={40}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontSize: 12, fontWeight: 600 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <ChartTooltip
              content={<ChartTooltipContent formatter={(value) => [`${value}%`, chartLabel]} />}
            />
            <Bar dataKey="pct" fill="var(--color-pct)" radius={[4, 4, 0, 0]} label={{ position: "top", formatter: (v: number) => `${v}%`, fontSize: 12, fontWeight: 700 }} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const statusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
  if (status === "active") return "default";
  if (status === "paused") return "outline";
  return "secondary";
};

type Period = '7d' | '30d';

const computePeriodDates = (daysBack: number) => {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  const daysAgo = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  return {
    currStart: fmt(daysAgo(daysBack - 1)),
    currEnd:   fmt(today),
    prevStart: fmt(daysAgo(daysBack * 2 - 1)),
    prevEnd:   fmt(daysAgo(daysBack)),
  };
};

const SOURCE_ICONS: Record<string, LucideIcon> = {
  youtube: Youtube,
  local: HardDrive,
  acervo: Library,
};

const SOURCE_COLORS: Record<string, string> = {
  youtube: "bg-red-500",
  local: "bg-indigo-500",
  acervo: "bg-green-500",
};

const formatChartDate = (value: string) => {
  const [, month, day] = value.split("-");
  return day && month ? `${day}/${month}` : value;
};

const formatPct = (value: number) =>
  Number.isInteger(value) ? value.toString() : value.toFixed(1);

const sumSeries = (series?: Array<{ total: number }>) =>
  (series ?? []).reduce((acc, p) => acc + p.total, 0);

const SectionHeader = ({
  title, icon: Icon, isOpen, onToggle,
}: {
  title: string; icon: React.ElementType; isOpen: boolean; onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between py-3 px-3 rounded-lg group hover:bg-muted/40 transition-colors"
  >
    <div className="flex items-center gap-2.5">
      <div className="p-1 rounded-md bg-muted">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <span className="text-sm font-semibold text-foreground/70 group-hover:text-foreground transition-colors">
        {title}
      </span>
    </div>
    <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
  </button>
);

const AdminResultados = () => {
  const { t } = useTranslation();
  const [activePeriod, setActivePeriod] = useState<Period>('30d');
  const [openSections, setOpenSections] = useState({
    kpis: true, conversion: true, users: false, campaigns: false, partners: false,
  });
  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const periodDays = activePeriod === '7d' ? 7 : 30;
  const { currStart, currEnd, prevStart, prevEnd } = computePeriodDates(periodDays);

  const { data: campaignsData, loading: campaignsLoading } = useAdminCpmCampaignsQuery({
    variables: { limit: 50, offset: 0 },
  });
  const { data: dashboardData } = useAdminResultsDashboardQuery({
    variables: { startDate: currStart, endDate: currEnd },
  });
  const { data: statsData } = useAdminStatsQuery();
  const { data: currPeriodData } = useAdminStatsQuery({
    variables: { startDate: currStart, endDate: currEnd },
  });
  const { data: prevPeriodData } = useAdminStatsQuery({
    variables: { startDate: prevStart, endDate: prevEnd },
  });

  const campaigns = campaignsData?.adminCpmCampaigns?.data ?? [];
  const dashboard = dashboardData?.adminResultsDashboard;
  const ownBrandCount = dashboard?.funnelCreator?.[0]?.count;
  const otherChannelsCount = dashboard?.funnelClipper?.[0]?.count;

  const funnelCreator: FunnelStep[] = (dashboard?.funnelCreator ?? []).map((step) => ({
    step: t(`admin.resultados.funnel.steps.${step.label}`, { defaultValue: step.label }),
    users: step.count,
    pct: step.pct,
  }));
  const funnelClipper: FunnelStep[] = (dashboard?.funnelClipper ?? []).map((step) => ({
    step: t(`admin.resultados.funnel.steps.${step.label}`, { defaultValue: step.label }),
    users: step.count,
    pct: step.pct,
  }));
  const sourcesCreator: SourceItem[] = (dashboard?.sourcesCreator ?? []).map((source) => ({
    key: source.key,
    label: t(`admin.resultados.sources.${source.key}`, { defaultValue: source.label }),
    value: source.pct,
    icon: SOURCE_ICONS[source.key] ?? Library,
    colorClass: SOURCE_COLORS[source.key] ?? "bg-slate-500",
  }));
  const sourcesClipper: SourceItem[] = (dashboard?.sourcesClipper ?? []).map((source) => ({
    key: source.key,
    label: t(`admin.resultados.sources.${source.key}`, { defaultValue: source.label }),
    value: source.pct,
    icon: SOURCE_ICONS[source.key] ?? Library,
    colorClass: SOURCE_COLORS[source.key] ?? "bg-slate-500",
  }));
  const retentionCreator = (dashboard?.retentionCreator ?? []).map(({ period, pct }) => ({ period, pct }));
  const retentionClipper = (dashboard?.retentionClipper ?? []).map(({ period, pct }) => ({ period, pct }));
  const activityData = (dashboard?.activity ?? []).map(({ date, users, clips }) => ({
    date: formatChartDate(date),
    usuarios: users,
    clipes: clips,
  }));
  const conversion = dashboard?.conversion ?? { totalFree: 0, totalPaid: 0, rate: 0 };
  const campaignSummary = dashboard?.campaigns ?? {
    enrolled: 0,
    submitted: 0,
    approved: 0,
    earningsCents: 0,
    clipperToPaidPct: 0,
    subscriberToCampaignPct: 0,
  };
  const nwc = dashboard?.nwc ?? {
    total: 0,
    paidPct: 0,
    campaignPct: 0,
    retentionD1: 0,
    retentionD7: 0,
    retentionD30: 0,
    organicRetentionD1: 0,
    organicRetentionD7: 0,
    organicRetentionD30: 0,
  };

  const realKpiValues: Record<string, number | undefined> = {
    users:     statsData?.adminStats?.users?.total,
    clips:     statsData?.adminStats?.clips?.total,
    downloads: statsData?.adminStats?.youtubeDownloads?.total,
    projects:  statsData?.adminStats?.projects?.total,
  };

  const periodDeltas: Record<string, number | undefined> = (() => {
    if (!currPeriodData || !prevPeriodData) return {};
    const curr = currPeriodData.adminStats;
    const prev = prevPeriodData.adminStats;
    const delta = (currVal: number, prevVal: number) =>
      prevVal > 0 ? ((currVal - prevVal) / prevVal) * 100 : 0;
    return {
      users:     delta(sumSeries(curr.users.timeSeries),            sumSeries(prev.users.timeSeries)),
      clips:     delta(sumSeries(curr.clips.timeSeries),            sumSeries(prev.clips.timeSeries)),
      downloads: delta(sumSeries(curr.youtubeDownloads.timeSeries), sumSeries(prev.youtubeDownloads.timeSeries)),
      projects:  delta(sumSeries(curr.projects.timeSeries),         sumSeries(prev.projects.timeSeries)),
    };
  })();

  const prevPeriodValues: Record<string, number | undefined> = (() => {
    if (!prevPeriodData) return {};
    const prev = prevPeriodData.adminStats;
    return {
      users:     sumSeries(prev.users.timeSeries),
      clips:     sumSeries(prev.clips.timeSeries),
      downloads: sumSeries(prev.youtubeDownloads.timeSeries),
      projects:  sumSeries(prev.projects.timeSeries),
    };
  })();

  const activityConfig = {
    usuarios: { label: t("admin.resultados.chart.users"), color: "hsl(var(--primary))" },
    clipes:   { label: t("admin.resultados.chart.clips"), color: "hsl(262 80% 60%)"    },
  } satisfies ChartConfig;

  const earningsBRL = (campaignSummary.earningsCents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const campaignItems: CampaignItem[] = [
    { key: "enrolled",  display: campaignSummary.enrolled.toLocaleString("pt-BR"),  icon: UserCheck,   color: "text-blue-500",   bg: "bg-blue-500/10"   },
    { key: "submitted", display: campaignSummary.submitted.toLocaleString("pt-BR"), icon: Send,        color: "text-purple-500", bg: "bg-purple-500/10" },
    { key: "approved",  display: campaignSummary.approved.toLocaleString("pt-BR"),  icon: CheckCircle, color: "text-green-500",  bg: "bg-green-500/10"  },
    { key: "earnings",  display: earningsBRL,                                        icon: DollarSign,  color: "text-yellow-600", bg: "bg-yellow-500/10" },
  ];

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart2 className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.resultados.title")}</h1>
                <p className="text-muted-foreground text-sm">{t("admin.resultados.subtitle")}</p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-border">

            {/* ── Seção: KPIs ──────────────────────────────────────── */}
            <div>
              <SectionHeader title={t("admin.resultados.tabs.overview")} icon={BarChart2} isOpen={openSections.kpis} onToggle={() => toggleSection('kpis')} />
              {openSections.kpis && (
                <div className="space-y-4 pb-6 pt-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{t("admin.resultados.kpi.periodLabel")}</span>
                    <div className="flex items-center gap-1.5">
                      {(["7d", "30d"] as Period[]).map((p) => (
                        <button
                          key={p}
                          onClick={() => setActivePeriod(p)}
                          className={cn(
                            "text-xs px-2.5 py-1 rounded-md border transition-colors",
                            activePeriod === p
                              ? "bg-primary text-primary-foreground border-primary"
                              : "text-muted-foreground border-border hover:border-muted-foreground"
                          )}
                        >
                          {t(`admin.resultados.kpi.period_${p}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {KPI_ITEMS.map(({ key, value: mockValue, delta: mockDelta, icon: Icon, color, bg }) => {
                      const value = realKpiValues[key] ?? mockValue;
                      const delta = periodDeltas[key] ?? mockDelta;
                      const vsLabel = activePeriod === '7d'
                        ? t("admin.resultados.vsLastWeek")
                        : t("admin.resultados.vsLastMonth");
                      return (
                        <Card key={key}>
                          <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                              {t(`admin.resultados.kpi.${key}`)}
                            </CardTitle>
                            <div className={`p-1.5 rounded-md ${bg}`}>
                              <Icon className={`w-4 h-4 ${color}`} />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">{value.toLocaleString("pt-BR")}</div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                              <DeltaBadge delta={delta} />
                              <p className="text-xs text-muted-foreground">{vsLabel}</p>
                              {prevPeriodValues[key] !== undefined && (
                                <p className="text-xs text-muted-foreground">
                                  · {prevPeriodValues[key]!.toLocaleString("pt-BR")}
                                </p>
                              )}
                            </div>
                            {key === "users" && ownBrandCount !== undefined && otherChannelsCount !== undefined && (
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                  <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                                  {ownBrandCount.toLocaleString("pt-BR")} {t("admin.resultados.kpi.ownBrand")}
                                </span>
                                <span className="text-muted-foreground text-[10px]">/</span>
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                  <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                                  {otherChannelsCount.toLocaleString("pt-BR")} {t("admin.resultados.kpi.otherChannels")}
                                </span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ── Seção: Conversão ─────────────────────────────────── */}
            <div>
              <SectionHeader title={t("admin.resultados.conversion.title")} icon={TrendingUp} isOpen={openSections.conversion} onToggle={() => toggleSection('conversion')} />
              {openSections.conversion && (
                <div className="pb-6 pt-2">
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base">{t("admin.resultados.conversion.title")}</CardTitle>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">{t("admin.resultados.conversion.tooltip")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-2">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-primary">{formatPct(conversion.rate)}%</p>
                          <p className="text-sm text-muted-foreground mt-1">{t("admin.resultados.conversion.rate")}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-3xl font-bold">{conversion.totalFree.toLocaleString("pt-BR")}</p>
                          <p className="text-sm text-muted-foreground mt-1">{t("admin.resultados.conversion.totalFree")}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-3xl font-bold">{conversion.totalPaid.toLocaleString("pt-BR")}</p>
                          <p className="text-sm text-muted-foreground mt-1">{t("admin.resultados.conversion.totalPaid")}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

            {/* ── Seção: Usuários ──────────────────────────────────── */}
            <div>
              <SectionHeader title={t("admin.resultados.tabs.users")} icon={Users} isOpen={openSections.users} onToggle={() => toggleSection('users')} />
              {openSections.users && (
                <div className="space-y-6 pb-6 pt-2">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FunnelCard
                      title={t("admin.resultados.funnel.creator.title")}
                      subtitle={t("admin.resultados.funnel.creator.subtitle")}
                      steps={funnelCreator}
                    />
                    <FunnelCard
                      title={t("admin.resultados.funnel.clipper.title")}
                      subtitle={t("admin.resultados.funnel.clipper.subtitle")}
                      steps={funnelClipper}
                    />
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                      {t("admin.resultados.sources.sectionTitle")}
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <SourcesCard
                        title={t("admin.resultados.sources.creator")}
                        sources={sourcesCreator}
                      />
                      <SourcesCard
                        title={t("admin.resultados.sources.clipper")}
                        sources={sourcesClipper}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {t("admin.resultados.retention.sectionTitle")}
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs">{t("admin.resultados.retention.tooltip")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-sm text-muted-foreground -mt-2 mb-4">{t("admin.resultados.retention.subtitle")}</p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <RetentionCard
                        title={t("admin.resultados.retention.creator")}
                        data={retentionCreator}
                        chartLabel={t("admin.resultados.retention.chartLabel")}
                      />
                      <RetentionCard
                        title={t("admin.resultados.retention.clipper")}
                        data={retentionClipper}
                        chartLabel={t("admin.resultados.retention.chartLabel")}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground text-center mt-3">
                      {t("admin.resultados.retention.source")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t("admin.resultados.chart.newUsers")}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ChartContainer config={activityConfig} className="h-[220px] w-full">
                          <BarChart data={activityData}>
                            <CartesianGrid vertical={false} />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Bar dataKey="usuarios" fill="var(--color-usuarios)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                          </BarChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t("admin.resultados.chart.clipsCreated")}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ChartContainer config={activityConfig} className="h-[220px] w-full">
                          <BarChart data={activityData}>
                            <CartesianGrid vertical={false} />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Bar dataKey="clipes" fill="var(--color-clipes)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                          </BarChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>

            {/* ── Seção: Campanhas ─────────────────────────────────── */}
            <div>
              <SectionHeader title={t("admin.resultados.tabs.campaigns")} icon={Trophy} isOpen={openSections.campaigns} onToggle={() => toggleSection('campaigns')} />
              {openSections.campaigns && (
                <div className="space-y-6 pb-6 pt-2">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <CardTitle className="text-base">{t("admin.resultados.campaigns.title")}</CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground">{t("admin.resultados.campaigns.subtitle")}</p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {campaignItems.map(({ key, display, icon: Icon, color, bg }) => (
                          <div key={key} className="flex flex-col items-center p-4 rounded-lg bg-muted/30 text-center">
                            <div className={`p-2 rounded-full ${bg} mb-2`}>
                              <Icon className={`w-4 h-4 ${color}`} />
                            </div>
                            <p className="text-lg font-bold">{display}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t(`admin.resultados.campaigns.${key}`)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col items-center p-4 rounded-lg bg-muted/30 text-center">
                          <div className="flex items-center gap-1.5 mb-2">
                            <p className="text-xs text-muted-foreground">{t("admin.resultados.campaigns.clipperToPaid")}</p>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">{t("admin.resultados.campaigns.clipperToPaidTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="text-3xl font-bold text-primary">{formatPct(campaignSummary.clipperToPaidPct)}%</p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground mb-2">{t("admin.resultados.campaigns.subscriberToCampaign")}</p>
                          <p className="text-3xl font-bold text-primary">{formatPct(campaignSummary.subscriberToCampaignPct)}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <BarChart2 className="w-4 h-4 text-primary" />
                        <CardTitle className="text-base">{t("admin.resultados.campaignPerformance.title")}</CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground">{t("admin.resultados.campaignPerformance.subtitle")}</p>
                    </CardHeader>
                    <CardContent>
                      {campaignsLoading ? (
                        <div className="space-y-3">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                              <Skeleton className="h-4 w-40" />
                              <Skeleton className="h-4 w-16 ml-auto" />
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-4 w-8" />
                            </div>
                          ))}
                        </div>
                      ) : campaigns.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">
                          {t("admin.resultados.campaignPerformance.noCampaigns")}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {campaigns.map((campaign) => {
                            const spentBRL = (campaign.budget_spent_cents / 100).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: campaign.currency ?? "BRL",
                            });
                            const totalBRL = (campaign.budget_total_cents / 100).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: campaign.currency ?? "BRL",
                            });
                            const budgetPct = campaign.budget_total_cents > 0
                              ? Math.min(100, Math.round((campaign.budget_spent_cents / campaign.budget_total_cents) * 100))
                              : 0;

                            return (
                              <div key={campaign.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Badge variant={statusBadgeVariant(campaign.status)} className="text-[10px] shrink-0">
                                    {t(`admin.campaigns.status.${campaign.status}`, { defaultValue: campaign.status })}
                                  </Badge>
                                  <span className="font-medium text-sm truncate">{campaign.name}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm shrink-0">
                                  <Scissors className="w-3.5 h-3.5 text-muted-foreground" />
                                  <span className="font-semibold">{campaign.submissions_count.toLocaleString("pt-BR")}</span>
                                  <span className="text-muted-foreground text-xs">{t("admin.resultados.campaignPerformance.submissions")}</span>
                                </div>
                                <div className="flex flex-col gap-1 w-full sm:w-44 shrink-0">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{spentBRL}</span>
                                    <span>{totalBRL}</span>
                                  </div>
                                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full transition-all duration-500"
                                      style={{ width: `${budgetPct}%` }}
                                    />
                                  </div>
                                  <span className="text-[11px] text-muted-foreground">{budgetPct}% {t("admin.resultados.campaignPerformance.spent")}</span>
                                </div>
                                <Link
                                  to={`/admin/campaigns/${campaign.id}/submissions`}
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  {t("admin.resultados.campaignPerformance.viewDetail")}
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

            {/* ── Seção: Parcerias ─────────────────────────────────── */}
            <div>
              <SectionHeader title={t("admin.resultados.tabs.partners")} icon={Handshake} isOpen={openSections.partners} onToggle={() => toggleSection('partners')} />
              {openSections.partners && (
                <div className="space-y-6 pb-6 pt-2">
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Handshake className="w-4 h-4 text-blue-500" />
                        <CardTitle className="text-base">{t("admin.resultados.nwc.title")}</CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground">{t("admin.resultados.nwc.subtitle")}</p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="flex flex-col items-center p-4 rounded-lg bg-blue-500/10 text-center">
                          <Users className="w-5 h-5 text-blue-500 mb-2" />
                          <p className="text-3xl font-bold">{nwc.total.toLocaleString("pt-BR")}</p>
                          <p className="text-xs text-muted-foreground mt-1">{t("admin.resultados.nwc.total")}</p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-green-500/10 text-center">
                          <TrendingUp className="w-5 h-5 text-green-500 mb-2" />
                          <p className="text-3xl font-bold text-green-600">{formatPct(nwc.paidPct)}%</p>
                          <p className="text-xs text-muted-foreground mt-1">{t("admin.resultados.nwc.paidPct")}</p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-lg bg-purple-500/10 text-center">
                          <Trophy className="w-5 h-5 text-purple-500 mb-2" />
                          <p className="text-3xl font-bold text-purple-600">{formatPct(nwc.campaignPct)}%</p>
                          <p className="text-xs text-muted-foreground mt-1">{t("admin.resultados.nwc.campaignPct")}</p>
                        </div>
                      </div>
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-4">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {t("admin.resultados.nwc.retentionTitle")}
                          </p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{t("admin.resultados.nwc.retentionTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="space-y-3">
                          {[
                            { label: "D+1",  nwc: nwc.retentionD1,  organic: nwc.organicRetentionD1  },
                            { label: "D+7",  nwc: nwc.retentionD7,  organic: nwc.organicRetentionD7  },
                            { label: "D+30", nwc: nwc.retentionD30, organic: nwc.organicRetentionD30 },
                          ].map(({ label, nwc, organic }) => (
                            <div key={label} className="space-y-1.5">
                              <span className="font-semibold text-sm">{label}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-blue-400 font-medium w-20 shrink-0">NWC {nwc}%</span>
                                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${nwc}%` }} />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400 font-medium w-20 shrink-0">{t("admin.resultados.nwc.organic")} {organic}%</span>
                                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{ width: `${organic}%` }} />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-3">{t("admin.resultados.nwc.retentionNote")}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
    </Layout>
  );
};

export default AdminResultados;
